import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { useQueries, useQuery } from '@tanstack/react-query';
import type { WorkspaceRoleString } from 'api';
import { WorkspaceRoleLabels, projectService, reportsService } from 'api';
import { useProjectActions } from 'features/projects/hooks/useProjectActions';
import {
  FILTER_FIELD_TEXT,
  MemberCellAvatar,
  MemberPrimaryText,
  MemberRowBox,
  MemberSecondaryText,
} from 'features/projects/projects.styles';
import { X } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import type { DataTableColumn } from 'shared/components/DataTable';
import { DataTable, TableScrollBox } from 'shared/components/DataTable';
import { queryKeys } from 'shared/constants/queryKeys';

import {
  ClientMemberStatusBadge,
  ClientTabContentStack,
  DrawerBadgeRow,
  DrawerBody,
  DrawerCard,
  DrawerCloseButton,
  DrawerContentStack,
  DrawerEmailText,
  DrawerHeader,
  DrawerIdentityRow,
  DrawerProjectBar,
  DrawerProjectHours,
  DrawerProjectList,
  DrawerProjectPercentText,
  DrawerProjectRow,
  DrawerRoleText,
  DrawerSectionLabel,
  DrawerStatLabel,
  DrawerStatsGrid,
  DrawerStatValue,
  DrawerTitle,
  DRAWER_AVATAR_SX,
  DRAWER_SUBTITLE_SX,
  FilterSelect,
  FilterToolbarCard,
  MEMBER_DRAWER_BACKDROP_SX,
  MEMBER_DRAWER_PAPER_SX,
  MEMBERS_CELL_PAD_LEFT_SX,
  MEMBERS_CELL_PAD_RIGHT_SX,
  MEMBERS_CELL_PAD_RIGHT_WIDE_SX,
  MEMBERS_PROJECT_FILTER_SX,
  MEMBERS_ROLE_FILTER_SX,
  MEMBERS_SEARCH_ICON_SX,
  MEMBERS_SECONDARY_TEXT_BLOCK_SX,
  MEMBERS_STATUS_CELL_SX,
  MEMBERS_STATUS_FILTER_SX,
  MEMBERS_TABLE_CONTAINER_SX,
  MEMBERS_TOOLBAR_SX,
  MEMBERS_TOTAL_CELL_SX,
  SearchFieldBox,
  SearchIconWrap,
  SearchInput,
  STATUS_MENU_ITEM_SX,
  STATUS_MENU_LIST_SX,
  STATUS_MENU_PAPER_SX,
} from '../clients.styles';

import { formatTrackedHM } from './ClientProjectsTab';

interface Props {
  clientId: string;
  clientName: string;
}

interface AggregatedMember {
  userId: string;
  name: string;
  email: string;
  avatarUrl?: string;
  role: WorkspaceRoleString;
  isActive: boolean;
  projectIds: Set<string>;
}

type StatusFilter = 'all' | 'active' | 'inactive';

const ROLE_OPTIONS: WorkspaceRoleString[] = ['Owner', 'Admin', 'ProjectManager', 'TeamMember'];

// Same "all time" range hack the Projects tab / ProjectDetailPage use for all-time stats.
const ALL_TIME_FROM = '2000-01-01';

// The design's mock data models a per-project job-title "role" (Designer, Developer, …)
// with a member potentially holding several across projects. The real backend has no such
// concept — a member has exactly one workspace role (Owner/Admin/ProjectManager/TeamMember)
// that doesn't vary by project — so "Role" here is just that single role's label, shown
// as plain text matching design's own `text-text-secondary` markup (not a colored pill —
// only Status is a chip in this design).
export const ClientMembersTab: React.FC<Props> = ({ clientId, clientName }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [roleFilter, setRoleFilter] = useState<'all' | WorkspaceRoleString>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [projectFilter, setProjectFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [selectedMember, setSelectedMember] = useState<AggregatedMember | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { projects } = useProjectActions();
  const clientProjects = useMemo(
    () => projects.filter((p) => p.clientId === clientId),
    [projects, clientId],
  );
  const projectNameById = useMemo(
    () => new Map(clientProjects.map((p) => [p.id, p.name])),
    [clientProjects],
  );

  // Same per-project members endpoint the Projects tab / ProjectAccessTab use — aggregated
  // here across every project under this client instead of scoped to just one.
  const memberQueries = useQueries({
    queries: clientProjects.map((project) => ({
      queryKey: queryKeys.projects.members(project.id),
      queryFn: () => projectService.getProjectMembers(project.id).then((r) => r.data.data),
    })),
  });

  const aggregatedMembers = useMemo(() => {
    const map = new Map<string, AggregatedMember>();
    clientProjects.forEach((project, index) => {
      const members = memberQueries[index]?.data ?? [];
      members.forEach((m) => {
        const existing = map.get(m.userId);
        if (existing) {
          existing.projectIds.add(project.id);
        } else {
          map.set(m.userId, {
            userId: m.userId,
            name: m.name,
            email: m.email,
            avatarUrl: m.avatarUrl,
            role: m.role,
            isActive: m.workspaceMembershipStatus !== 'Inactive',
            projectIds: new Set([project.id]),
          });
        }
      });
    });
    return Array.from(map.values());
  }, [clientProjects, memberQueries]);

  // One aggregate query for every member's total time across this whole client — same
  // reports endpoint the Projects tab uses per-project, just scoped by clientId instead.
  const { data: clientMemberTotals = [] } = useQuery({
    queryKey: queryKeys.reports.summary({ clientId, groupBy: 'user', from: ALL_TIME_FROM, to: today }),
    queryFn: () =>
      reportsService
        .getSummary({ clientId, groupBy: 'user', from: ALL_TIME_FROM, to: today })
        .then((r) => r.data.data.groups),
    enabled: clientProjects.length > 0,
  });

  const totalSecondsByUserId = useMemo(() => {
    const map = new Map<string, number>();
    clientMemberTotals.forEach((g) => {
      if (g.key) map.set(g.key, g.totalDurationSecs);
    });
    return map;
  }, [clientMemberTotals]);

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return aggregatedMembers.filter((m) => {
      const matchesRole = roleFilter === 'all' || m.role === roleFilter;
      const matchesStatus =
        statusFilter === 'all' || (statusFilter === 'active' ? m.isActive : !m.isActive);
      const matchesProject = projectFilter === 'all' || m.projectIds.has(projectFilter);
      const matchesSearch =
        !q || m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q);
      return matchesRole && matchesStatus && matchesProject && matchesSearch;
    });
  }, [aggregatedMembers, roleFilter, statusFilter, projectFilter, search]);

  const openMember = (member: AggregatedMember) => {
    setSelectedMember(member);
    setDrawerOpen(true);
  };

  // Per-project breakdown for the drawer — same endpoint, grouped by project and scoped
  // to just the selected member, lazy-loaded only while the drawer is open.
  const { data: memberProjectBreakdown = [] } = useQuery({
    queryKey: queryKeys.reports.summary({
      clientId,
      groupBy: 'project',
      userIds: selectedMember?.userId,
      from: ALL_TIME_FROM,
      to: today,
    }),
    queryFn: () =>
      reportsService
        .getSummary({
          clientId,
          groupBy: 'project',
          userIds: selectedMember!.userId,
          from: ALL_TIME_FROM,
          to: today,
        })
        .then((r) => r.data.data.groups),
    enabled: Boolean(selectedMember) && drawerOpen,
  });

  const selectedMemberTotal = selectedMember
    ? (totalSecondsByUserId.get(selectedMember.userId) ?? 0)
    : 0;

  const columns: DataTableColumn<AggregatedMember>[] = [
    {
      id: 'name',
      label: (
        <Box component="span" sx={MEMBERS_CELL_PAD_LEFT_SX}>
          EMPLOYEE
        </Box>
      ),
      render: (member) => (
        <MemberRowBox sx={MEMBERS_CELL_PAD_LEFT_SX}>
          <MemberCellAvatar src={member.avatarUrl} alt={member.name}>
            {member.name.charAt(0).toUpperCase()}
          </MemberCellAvatar>
          <Box minWidth={0}>
            <MemberPrimaryText variant="body2" noWrap>
              {member.name}
            </MemberPrimaryText>
            <MemberSecondaryText variant="caption" noWrap sx={MEMBERS_SECONDARY_TEXT_BLOCK_SX}>
              {member.email}
            </MemberSecondaryText>
          </Box>
        </MemberRowBox>
      ),
    },
    {
      id: 'projects',
      label: 'PROJECTS',
      align: 'center',
      width: 90,
      render: (member) => member.projectIds.size,
    },
    {
      // Design's ClientMembersTab renders Role as plain secondary text, not a
      // colored pill (unlike Status, which is a chip) — verified against the
      // actual `<span className="text-sm text-text-secondary">` markup.
      id: 'role',
      label: 'ROLE(S)',
      width: 150,
      render: (member) => (
        <Typography variant="body2" color="text.secondary" noWrap>
          {WorkspaceRoleLabels[member.role]}
        </Typography>
      ),
    },
    {
      id: 'total',
      label: (
        <Box component="span" sx={MEMBERS_CELL_PAD_RIGHT_WIDE_SX}>
          TOTAL
        </Box>
      ),
      align: 'right',
      width: 120,
      render: (member) => (
        <Box component="span" sx={MEMBERS_TOTAL_CELL_SX}>
          {formatTrackedHM(totalSecondsByUserId.get(member.userId) ?? 0)}
        </Box>
      ),
    },
    {
      // "Status" is the last visible column here (Members has no row-actions column
      // like the Projects tab does) — needs the same outer-right-edge compensation so
      // the pill doesn't sit flush against the card's right border.
      id: 'status',
      label: (
        <Box component="span" sx={MEMBERS_CELL_PAD_RIGHT_SX}>
          STATUS
        </Box>
      ),
      width: 110,
      render: (member) => (
        <Box component="span" sx={MEMBERS_STATUS_CELL_SX}>
          <ClientMemberStatusBadge
            active={member.isActive}
            label={member.isActive ? 'Active' : 'Exited'}
            memberLabel={member.isActive ? 'Active' : 'Exited'}
            size="small"
          />
        </Box>
      ),
    },
  ];

  return (
    <ClientTabContentStack>
      <FilterToolbarCard sx={MEMBERS_TOOLBAR_SX}>
        <FilterSelect
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value as 'all' | WorkspaceRoleString)}
          IconComponent={KeyboardArrowDownIcon}
          sx={MEMBERS_ROLE_FILTER_SX}
          MenuProps={{
            PaperProps: { sx: STATUS_MENU_PAPER_SX },
            MenuListProps: { sx: STATUS_MENU_LIST_SX },
          }}
        >
          <MenuItem value="all" sx={STATUS_MENU_ITEM_SX}>
            All roles
          </MenuItem>
          {ROLE_OPTIONS.map((role) => (
            <MenuItem key={role} value={role} sx={STATUS_MENU_ITEM_SX}>
              {WorkspaceRoleLabels[role]}
            </MenuItem>
          ))}
        </FilterSelect>

        <FilterSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          IconComponent={KeyboardArrowDownIcon}
          sx={MEMBERS_STATUS_FILTER_SX}
          MenuProps={{
            PaperProps: { sx: STATUS_MENU_PAPER_SX },
            MenuListProps: { sx: STATUS_MENU_LIST_SX },
          }}
        >
          <MenuItem value="all" sx={STATUS_MENU_ITEM_SX}>
            All statuses
          </MenuItem>
          <MenuItem value="active" sx={STATUS_MENU_ITEM_SX}>
            Active
          </MenuItem>
          <MenuItem value="inactive" sx={STATUS_MENU_ITEM_SX}>
            Inactive
          </MenuItem>
        </FilterSelect>

        <FilterSelect
          value={projectFilter}
          onChange={(e) => setProjectFilter(e.target.value as string)}
          IconComponent={KeyboardArrowDownIcon}
          sx={MEMBERS_PROJECT_FILTER_SX}
          MenuProps={{
            PaperProps: { sx: STATUS_MENU_PAPER_SX },
            MenuListProps: { sx: STATUS_MENU_LIST_SX },
          }}
        >
          <MenuItem value="all" sx={STATUS_MENU_ITEM_SX}>
            All projects
          </MenuItem>
          {clientProjects.map((project) => (
            <MenuItem key={project.id} value={project.id} sx={STATUS_MENU_ITEM_SX}>
              {project.name}
            </MenuItem>
          ))}
        </FilterSelect>

        <SearchFieldBox>
          <SearchIconWrap>
            <SearchIcon sx={MEMBERS_SEARCH_ICON_SX} />
          </SearchIconWrap>
          <SearchInput
            placeholder="Search members"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchFieldBox>
      </FilterToolbarCard>

      <TableScrollBox>
        <DataTable<AggregatedMember>
          columns={columns}
          rows={filteredMembers}
          getRowId={(member) => member.userId}
          onRowClick={(userId) => {
            const member = filteredMembers.find((m) => m.userId === userId);
            if (member) openMember(member);
          }}
          emptyMessage="No members match your filters."
          borderless
          fixedLayout
          containerSx={MEMBERS_TABLE_CONTAINER_SX}
          headerBgColor="#F3F0FF"
          headerColor={FILTER_FIELD_TEXT}
          headerWeight={500}
          headerFontSize="10px"
          headerHeight={36}
          cellPaddingX={6}
          rowHeight={64}
          rowHoverColor="rgba(110, 79, 247, 0.03)"
        />
      </TableScrollBox>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        slotProps={{ backdrop: { sx: MEMBER_DRAWER_BACKDROP_SX } }}
        PaperProps={{ sx: MEMBER_DRAWER_PAPER_SX }}
      >
        <DrawerHeader>
          <Box minWidth={0}>
            <DrawerTitle noWrap>{selectedMember?.name}</DrawerTitle>
            {selectedMember && (
              <Typography variant="caption" sx={DRAWER_SUBTITLE_SX}>
                Projects for {clientName}
              </Typography>
            )}
          </Box>
          <DrawerCloseButton size="small" onClick={() => setDrawerOpen(false)}>
            <X size={16} />
          </DrawerCloseButton>
        </DrawerHeader>

        <DrawerBody>
          {selectedMember && (
            <DrawerContentStack>
              <DrawerIdentityRow>
                <MemberCellAvatar src={selectedMember.avatarUrl} alt={selectedMember.name} sx={DRAWER_AVATAR_SX}>
                  {selectedMember.name.charAt(0).toUpperCase()}
                </MemberCellAvatar>
                <Box minWidth={0}>
                  <DrawerBadgeRow>
                    <DrawerRoleText variant="body2">
                      {WorkspaceRoleLabels[selectedMember.role]}
                    </DrawerRoleText>
                    <ClientMemberStatusBadge
                      active={selectedMember.isActive}
                      label={selectedMember.isActive ? 'Active' : 'Exited'}
                      memberLabel={selectedMember.isActive ? 'Active' : 'Exited'}
                      size="small"
                    />
                  </DrawerBadgeRow>
                  <DrawerEmailText variant="caption" noWrap>
                    {selectedMember.email}
                  </DrawerEmailText>
                </Box>
              </DrawerIdentityRow>

              <DrawerStatsGrid>
                <DrawerCard>
                  <DrawerStatLabel variant="caption">Total</DrawerStatLabel>
                  <DrawerStatValue variant="body2" accent>
                    {formatTrackedHM(selectedMemberTotal)}
                  </DrawerStatValue>
                </DrawerCard>
                <DrawerCard>
                  <DrawerStatLabel variant="caption">Projects</DrawerStatLabel>
                  <DrawerStatValue variant="body2">{selectedMember.projectIds.size}</DrawerStatValue>
                </DrawerCard>
              </DrawerStatsGrid>

              <Box>
                <DrawerSectionLabel variant="caption">Hours per project</DrawerSectionLabel>
                <DrawerProjectList>
                  {[...memberProjectBreakdown]
                    .sort((a, b) => b.totalDurationSecs - a.totalDurationSecs)
                    .map((group) => {
                      const pct =
                        selectedMemberTotal > 0
                          ? Math.round((group.totalDurationSecs / selectedMemberTotal) * 100)
                          : 0;
                      return (
                        <DrawerCard key={group.key}>
                          <DrawerProjectRow>
                            <Typography variant="body2" fontWeight={500} noWrap>
                              {(group.key && projectNameById.get(group.key)) || 'Unknown project'}
                            </Typography>
                            <DrawerProjectHours variant="body2" fontWeight={600}>
                              {formatTrackedHM(group.totalDurationSecs)}
                            </DrawerProjectHours>
                          </DrawerProjectRow>
                          <DrawerProjectBar variant="determinate" value={pct} />
                          <DrawerProjectPercentText variant="caption">
                            {pct}% of client hours
                          </DrawerProjectPercentText>
                        </DrawerCard>
                      );
                    })}
                </DrawerProjectList>
              </Box>
            </DrawerContentStack>
          )}
        </DrawerBody>
      </Drawer>
    </ClientTabContentStack>
  );
};
